/**
 * ConnectPayee — "this bank description is that Core payee."
 *
 * Lives under Review, alongside the account decisions, because it IS a decision about the vendor:
 * Matt, 2026-09-16, "like we assign the accounts there, wouldn't it make sense to do that for
 * Vendors when clarity is lacking there, too?"
 *
 * Search what Core already has, pick one, and the memory learns it — every transaction carrying
 * this description posts from then on. Nothing is written to Core here; it is a row in Cortex and
 * it can be undone. That is why this is safe to hand to whoever is doing the books, and why it
 * must never be confused with creating a NEW vendor, which stays in Core where duplicates are
 * checked by a human.
 */
import { useState } from 'react'
import { searchCorePayees, linkPayee } from '../../services/financeImport.js'

export default function ConnectPayee({ S, pattern, onLinked, inline = false }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState(null)
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')

  async function search(term) {
    setQ(term); setErr('')
    if (term.trim().length < 2) { setHits(null); return }
    setBusy('search')
    try { setHits((await searchCorePayees(term)).matches || []) }
    catch (e) { setErr(e.message || String(e)) }
    finally { setBusy('') }
  }

  async function pick(p) {
    setBusy('link'); setErr('')
    try {
      await linkPayee(pattern, p, 'Connected under Review')
      setOpen(false); setQ(''); setHits(null)
      onLinked(p)
    } catch (e) { setErr(e.message || String(e)) }
    finally { setBusy('') }
  }

  if (!open) {
    return (
      <button style={{ ...S.btn, fontSize: 12, padding: '4px 10px' }} onClick={() => setOpen(true)}>
        Connect to a vendor Core already has…
      </button>
    )
  }
  return (
    <div style={{ padding: 8, border: '0.5px solid var(--aj-border)', borderRadius: 6, background: 'var(--aj-bg-subtle)', minWidth: 320 }}>
      <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 6 }}>
        Type the vendor's real name — the one Core knows it by.
      </div>
      <input
        autoFocus value={q} onChange={e => search(e.target.value)}
        placeholder="e.g. Slack" style={{ ...S.input, width: 240, fontFamily: 'DM Sans, sans-serif' }}
      />
      <button style={{ ...S.btn, fontSize: 11, padding: '3px 8px', marginLeft: 6 }} onClick={() => { setOpen(false); setQ(''); setHits(null) }}>Cancel</button>
      {busy === 'search' && <span style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginLeft: 8 }}>Searching…</span>}
      {err && <div style={{ fontSize: 12, color: '#b4493c', marginTop: 6 }}>{err}</div>}
      {hits && (
        <div style={{ marginTop: 6 }}>
          {hits.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>
              Nothing in Core is close to that. If this is a new vendor, create it in Core first, then
              press Re-check.
            </div>
          )}
          {hits.slice(0, 8).map(p => (
            <button
              key={p.id} disabled={!!busy} onClick={() => pick(p)}
              style={{ ...S.btn, display: 'block', width: '100%', textAlign: 'left', fontSize: 12, padding: '5px 8px', marginTop: 3 }}
            >
              {p.name}
              <span style={{ ...S.mono, fontSize: 10, color: 'var(--aj-text-muted)', marginLeft: 8 }}>{p.kind}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
